import type { Attachment, Patient } from '@medplum/fhirtypes'
import { Button, Modal } from '@mantine/core'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type { ChartItem } from '../PatientChart.utils.tsx'
import { buildPatientChartRelativePath, parsePatientChartSplat } from '../../patientChartPath'
import { NoteDetail } from './NoteDetail'
import { NoteForm } from './NoteForm'
import { NotesList } from './NotesList'
import { useCareCoordinationNotes } from './useCareCoordinationNotes'
import { useCreateCareCoordinationNote } from './useCreateCareCoordinationNote'
import { useUpdateCareCoordinationNote } from './useUpdateCareCoordinationNote'

type Props = {
  patient: Patient
  item: ChartItem
  refetchPatient: () => void
}

export function CareCoordinationNotes({ patient }: Props) {
  const params = useParams()
  const navigate = useNavigate()
  const [selectedNoteId, setSelectedNoteId] = useState<string | undefined>()
  const [createOpen, setCreateOpen] = useState(false)

  const { notes, fetchNextPage, hasNextPage, isLoading } = useCareCoordinationNotes(patient.id!)

  const createNote = useCreateCareCoordinationNote(patient)
  const updateNote = useUpdateCareCoordinationNote(patient)

  // Deep-link selection: /patients/<patientId>/care-coordination-notes/<communicationId>
  // Depends on params only: with selectedNoteId in the deps, a click-selection fires the
  // effect before the deferred URL update lands and briefly reverts to the note
  // still in the old URL. React no-ops identical state updates, so no guard needed.
  useEffect(() => {
    const noteId = parsePatientChartSplat(params['*']).resourceId
    if (noteId) {
      setSelectedNoteId(noteId)
    }
  }, [params])

  // Relative path: the chart is hosted under both `/patients/:patientId/*` and
  // `/call/:encounterId/*` — an absolute `/patients/...` path would yank the
  // user off the call page (see ListLayout for the same pattern).
  const selectNote = (noteId: string | undefined) => {
    setSelectedNoteId(noteId)
    if (noteId) {
      void navigate(buildPatientChartRelativePath('care-coordination-notes', noteId))
    }
  }

  const selectedNote = notes.find((n) => n.id === selectedNoteId) ?? notes[0]

  const handleCreate = async (note: string, nextAction: string, files: File[]) => {
    await createNote.mutateAsync(
      { note, nextAction, files },
      {
        onSuccess: (created) => {
          selectNote(created.id)
          setCreateOpen(false)
        },
      },
    )
  }

  const handleUpdate = async (
    noteId: string,
    note: string,
    nextAction: string,
    files: File[],
    keptAttachments: Attachment[],
  ) => {
    await updateNote.mutateAsync(
      { noteId, note, nextAction, files, keptAttachments },
      {
        onSuccess: (updated) => selectNote(updated.id),
      },
    )
  }

  return (
    <>
      <div className="flex w-full items-start gap-2">
        <NotesList
          notes={notes}
          selectedId={selectedNote?.id}
          hasMore={!!hasNextPage}
          isLoading={isLoading}
          onSelect={(note) => selectNote(note.id)}
          onLoadMore={fetchNextPage}
        />

        <div className="ml-[280px] flex-1" style={{ width: 'calc(100% - 280px)' }}>
          <div className="flex justify-end p-4">
            <Button onClick={() => setCreateOpen(true)}>Add Note</Button>
          </div>

          {selectedNote ? (
            <NoteDetail note={selectedNote} onUpdate={handleUpdate} />
          ) : (
            <div className="flex h-48 items-center justify-center text-sm text-gray-400">
              {isLoading
                ? 'Loading note...'
                : notes.length === 0
                  ? 'No notes yet. Add the first one.'
                  : 'Select a note to view'}
            </div>
          )}
        </div>
      </div>

      <Modal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Add Care Coordination Note"
        size="lg"
      >
        <NoteForm onSubmit={handleCreate} onCancel={() => setCreateOpen(false)} />
      </Modal>
    </>
  )
}
